<template>
  <div class="min-h-screen bg-gray-50">
    <Head title="Dashboard" />
    
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div class="flex items-center justify-between mb-8">
        <h1 class="text-3xl font-bold text-gray-900">Escritorio</h1>
        <div class="text-sm text-gray-500">
          {{ currentDate }}
        </div>
      </div>

      <div v-if="role === 'admin'" class="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        <box-dashboard 
          :title="'Gastos del mes (Bs.)'"
          :loading="loading"
          class="bg-white shadow-sm rounded-lg hover:shadow-md transition-shadow duration-200"
        >
          <template #icon>
            <svg class="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </template>
          <p class="text-center text-3xl font-black text-gray-900">
            Bs. {{ formatCurrency(spentTotal) }}
          </p>
        </box-dashboard>

        <box-dashboard 
          :title="'Cuentas por cobrar'"
          :loading="loading"
          class="bg-white shadow-sm rounded-lg hover:shadow-md transition-shadow duration-200"
        >
          <template #icon>
            <svg class="w-6 h-6 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </template>
          <p class="text-center text-3xl font-black text-gray-900">
            ${{ formatCurrency(orderTotal) }}
          </p>
        </box-dashboard>

        <box-dashboard 
          v-if="role === 'financial' || role === 'admin'"
          :title="'Recaudado'"
          :loading="loading"
          class="bg-white shadow-sm rounded-lg hover:shadow-md transition-shadow duration-200"
        >
          <template #icon>
            <svg class="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </template>
          <p class="text-center text-3xl font-black text-gray-900">
            ${{ formatCurrency(balanceTotal) }}
          </p>
        </box-dashboard>

        <box-dashboard 
          :title="'Total de Propietarios'"
          :loading="loading"
          class="bg-white shadow-sm rounded-lg hover:shadow-md transition-shadow duration-200"
        >
          <template #icon>
            <svg class="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </template>
          <p class="text-center text-3xl font-black text-gray-900">
            {{ formatNumber(clientTotal) }}
          </p>
        </box-dashboard>
      </div>

      <!-- Sección Informativa: Estado de Cuenta -->
      <div class="mt-8 bg-white rounded-lg shadow-sm border border-gray-200 p-6 md:p-8">
        <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div class="flex-1 lg:max-w-2xl">
            <h2 class="text-xl font-bold text-gray-900 flex items-center mb-2">
              <svg class="h-6 w-6 text-indigo-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Portal Público de Estado de Cuenta
            </h2>
            <p class="text-sm text-gray-600 mb-4 leading-relaxed">
              La plataforma ofrece un portal público que permite a los copropietarios consultar su balance pendiente, revisar el listado de cuotas emitidas y registrar pagos directamente sin necesidad de iniciar sesión en el sistema administrativo.
            </p>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-2">
              <div class="flex items-start">
                <div class="flex-shrink-0 mt-0.5">
                  <span class="flex items-center justify-center h-5 w-5 rounded-full bg-indigo-100 text-indigo-600 text-xs font-bold">1</span>
                </div>
                <div class="ml-3">
                  <h3 class="text-xs font-bold text-gray-900 uppercase tracking-wider">¿Cómo funciona?</h3>
                  <p class="text-xs text-gray-500 mt-1 leading-relaxed">
                    El propietario ingresa a la ruta pública <a href="/estado-cuenta" class="text-indigo-600 hover:underline font-semibold">/estado-cuenta</a>, digita su documento de identidad y el sistema automáticamente busca y despliega su estado de cuenta individual actual.
                  </p>
                </div>
              </div>
              <div class="flex items-start">
                <div class="flex-shrink-0 mt-0.5">
                  <span class="flex items-center justify-center h-5 w-5 rounded-full bg-indigo-100 text-indigo-600 text-xs font-bold">2</span>
                </div>
                <div class="ml-3">
                  <h3 class="text-xs font-bold text-gray-900 uppercase tracking-wider">¿Cómo compartirlo?</h3>
                  <p class="text-xs text-gray-500 mt-1 leading-relaxed">
                    Puedes copiar el enlace directo de consulta o compartir la dirección pública <code class="bg-gray-100 px-1.5 py-0.5 rounded text-indigo-700 font-mono text-xs">/estado-cuenta</code> por WhatsApp, correos o circulares para que cada propietario consulte su saldo usando su cédula.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div class="mt-6 lg:mt-0 lg:ml-8 flex-shrink-0 flex flex-col sm:flex-row lg:flex-col space-y-3 sm:space-y-0 sm:space-x-3 lg:space-x-0 lg:space-y-3 w-full lg:w-auto">
            <a
              href="/estado-cuenta"
              target="_blank"
              class="w-full text-center inline-flex items-center justify-center px-4 py-2 border border-indigo-600 rounded-md text-sm font-medium text-indigo-600 bg-white hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
            >
              <svg class="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              Ir al Portal de Consulta
            </a>
            <button
              @click="copyDirectLink"
              class="w-full text-center inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
            >
              <svg class="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
              </svg>
              Copiar Enlace
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Head } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import BoxDashboard from '../../Shared/BoxDashboard.vue'

export default {
  components: {
    Head,
    BoxDashboard,
  },
  layout: Layout,
  props: {
    total: Number,
    orders: Object,
    clientTotal: Number,
    balanceTotal: Number,
    products: Number,
    orderTotal: Number,
    spentTotal: Number,
    productsSoldout: Object,
    orderMonthTotal: Number
  },
  data() {
    return {
      loading: false,
      role: this.$parent.auth.user.role,
    }
  },
  computed: {
    currentDate() {
      return new Date().toLocaleDateString('es-VE', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }
  },
  methods: {
    formatCurrency(value) {
      return new Intl.NumberFormat('es-VE', {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value)
    },
    formatNumber(value) {
      return new Intl.NumberFormat('es-VE').format(value)
    },
    getDate(date) {
      const dateNew = new Date(date)
      return dateNew.toLocaleString('es-VE', { hour12: false })
    },
    copyDirectLink() {
      const link = window.location.origin + '/estado-cuenta';
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(link).then(() => {
          alert('¡Enlace copiado al portapapeles! Comparte ' + link + ' con los propietarios.');
        }).catch(err => {
          this.fallbackCopyTextToClipboard(link);
        });
      } else {
        this.fallbackCopyTextToClipboard(link);
      }
    },
    fallbackCopyTextToClipboard(text) {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      
      // Avoid scrolling to bottom
      textArea.style.top = "0";
      textArea.style.left = "0";
      textArea.style.position = "fixed";

      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        const successful = document.execCommand('copy');
        if (successful) {
          alert('¡Enlace copiado al portapapeles! Comparte ' + text + ' con los propietarios.');
        } else {
          alert('No se pudo copiar el enlace automáticamente. Por favor cópialo manualmente: ' + text);
        }
      } catch (err) {
        alert('No se pudo copiar el enlace automáticamente. Por favor cópialo manualmente: ' + text);
      }

      document.body.removeChild(textArea);
    }
  }
}
</script>

<style scoped>
.transition-shadow {
  transition-property: box-shadow;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}
</style>
